import React, { Suspense, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import NavBar from "./Navbar/NavBar";
import { useTranslation } from "react-i18next";
import Heroimage1 from "../images/heroimage.jpg";

const LazyHeroImage = ({ onImageLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleLoad = (e) => {
    setIsLoaded(true);
    if (onImageLoad) onImageLoad();
  };

  return (
    <>
      {/* Blurred placeholder image */} 
      <img
        src={Heroimage1}
        alt=""
        className={`absolute top-0 right-0 w-auto min-w-full h-full object-cover object-[71%_100%] transition-opacity duration-700 blur-md scale-105 ${
          isLoaded ? "opacity-0" : "opacity-100"
        }`}
        style={{ filter: "blur(20px)" }}
      />
      
      {/* Sharp main image */}
      <img
        src={Heroimage1}
        alt="Hero Background"
        loading="lazy"
        decoding="async"
        className={`absolute top-0 right-0 w-auto min-w-full h-full object-cover object-[71%_100%] transition-opacity duration-700 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={handleLoad}
      />
    </>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      {/* Navbar */}
      <NavBar />

      {/* Hero Image with blur loading */}
      <Suspense fallback={<div className="absolute inset-0 " />}>
        <LazyHeroImage onImageLoad={() => setImageLoaded(true)} />
      </Suspense>

      {/* Hero Content */}
      <div className="relative z-20 h-full flex flex-col justify-between md:justify-center">
        <div className="container mx-auto px-6 lg:px-12 flex flex-col justify-between md:justify-center h-full items-center md:items-start text-center md:text-left">
          <div className="space-y-8 pt-8 lg:pt-20 w-full flex flex-col h-full md:h-auto">
            {/* Ligne supérieure */}
            <div className="flex items-center justify-center md:justify-start mb-6"></div>

            {/* Titre animé */}
            <motion.div
              className="text-center md:text-left"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <h1
                className="text-3xl sm:text-4xl md:text-5xl font-normal leading-snug text-[#225601] md:text-white"
                style={{ fontFamily: "Nephilm" }}
              >
                <motion.span
                  className="italic text-[#225601] md:text-white block md:inline"
                  style={{ fontFamily: "Nephilm" }}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
                >
                  {t("Tunisian excellence")}
                </motion.span>

                <motion.div
                  className="text-3xl sm:text-3xl md:text-4xl font-[Nephilmitalic] text-[#225601] md:text-white mt-2"
                  style={{ fontFamily: "Nephilm" }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut", delay: 1 }}
                >
                  {t("in every drop")}
                </motion.div>
              </h1>
            </motion.div>

            {/* Texte descriptif */}
            <motion.p
              className="hidden md:block text-lg md:text-xl font-normal text-white max-w-lg mx-auto md:mx-0 leading-relaxed text-center md:text-left"
              style={{ fontFamily: "Inter18light" }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.8 }}
            >
              {t("From tree to table – naturally tunisian olive oil")}
              <br />
              <span className="text-base font-medium mt-2 block">
                {t("Experience the purity of nature captured in every moment")}
              </span>
            </motion.p>

            {/* Boutons */}
            <motion.div
              className="absolute bottom-8 left-0 right-0 flex flex-col items-stretch justify-center space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6 px-1 md:static md:justify-start md:pb-0 z-30"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut", delay: 1 }}
            >
              {/* Bouton 1 */}
              <motion.div
                initial={{ opacity: 0, x: -30, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 1.2 }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact"
                  className="relative w-full sm:w-auto px-10 py-3 bg-white md:bg-black text-black md:text-white rounded-lg transition-all duration-700 ease-in-out hover:bg-gradient-to-r hover:from-[#225601] hover:to-[#2d6a03] hover:shadow-2xl hover:shadow-[#225601]/30 transform hover:-translate-y-2 uppercase tracking-wider text-base border-2 border-white md:border-black overflow-hidden group backdrop-blur-sm text-center"
                  style={{ fontFamily: "Nephilmitalic" }}
                >
                  <span className="relative z-10 font-medium">
                    {t("Book Your Consultation")}
                  </span>
                </Link>
              </motion.div>

              {/* Bouton 2 */}
              <motion.div
                initial={{ opacity: 0, x: 30, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 1.4 }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/AroundWorld"
                  className="relative w-full sm:w-auto px-10 py-3 border-2 border-white md:border-black text-white md:text-black bg-transparent hover:bg-gradient-to-r hover:from-[#225601] hover:to-[#2d6a03] hover:text-white rounded-lg transition-all duration-700 ease-in-out hover:shadow-2xl hover:shadow-[#225601]/30 transform hover:-translate-y-2 uppercase tracking-wider text-base overflow-hidden group backdrop-blur-sm text-center"
                  style={{ fontFamily: "Nephilmitalic" }}
                >
                  <span className="relative z-10">{t("About Us")}</span>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Ondulation de bas */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden h-20 md:h-29">
        <svg
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#272e02"
            d="M 0 320 C 471 73 1168 300 1443 0 L 1440 320 Z"
          />
        </svg>
      </div>
    </div>
  );
};

export default Hero;